// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PageContent } from '@/components/page-content';
import { ErrorRounded } from '@/constants/hardfin-standard-icons';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { AssetPresenceReportEducationalTitle } from '@/report/components/asset-presence-report-educational-title';
import { AssetReportLoader } from '@/report/components/asset-report-loader';
import { AssetTransitReportTable } from '@/report/components/asset-transit-report-table';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { ReportDateRangeInput } from '@/report/components/report-date-range-input';
import {
  ASSET_PRESENCE_REPORT_DATE_RANGE_SHORTCUTS,
  ASSET_PRESENCE_REPORT_MAX_DAYS,
} from '@/report/constants/asset-presence-date-range';
import { useAssetTransitReport } from '@/report/hooks/use-asset-transit-report';
import { getReportFileName } from '@/report/utilities/get-report-file-name';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

const REPORT_TITLE = 'Transit asset report';

const searchSchema = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
});

// A stable identity, so the grid does not rebuild its row tree.
const EMPTY_ROWS: never[] = [];

export const Route = createFileRoute('/_app/reports/transit')({
  component: TransitReportRoute,
  validateSearch: searchSchema,
});

function TransitReportRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { user } = useCurrentUser();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useResizeObserver(containerRef);

  // Only run the report query when a date range is explicitly selected
  const hasDateRange = search.start !== undefined && search.end !== undefined;

  const transitReport = useAssetTransitReport({
    ...search,
    enabled: hasDateRange,
  });

  // Track loader animation completion state
  const [isLoaderComplete, setIsLoaderComplete] = useState(false);

  // Reset loader when search params change
  useEffect(() => {
    setIsLoaderComplete(false);
  }, [search.start, search.end]);

  // Show loader during initial load, hide once complete
  const showLoader = transitReport.isLoading && !isLoaderComplete;

  const handleLoaderComplete = () => {
    setIsLoaderComplete(true);
  };

  const data = transitReport.data?.rows ?? EMPTY_ROWS;
  const isDateRangeError =
    transitReport.isError &&
    transitReport.error !== null &&
    handleAPIError(transitReport.error).errorType === 'date_range_exceeded';

  const dayCount = transitReport.interval.count('days');
  const isDateRangeTooLong = dayCount > ASSET_PRESENCE_REPORT_MAX_DAYS;

  const handleChangeDateRange = (dateRange: DateRange<DateTime>) => {
    const start = dateRange[0]?.toISODate();
    const end = dateRange[1]?.toISODate();

    navigate({ search: { ...search, start, end } });
  };

  const getFileName = useCallback(() => {
    return getReportFileName(
      'transit-asset-report',
      user.organization.timezone
    );
  }, [user.organization.timezone]);

  return (
    <>
      <PageContent
        sx={{
          gap: 2,
          flex: 1,
          display: 'grid',
          maxHeight: '100%',
          gridTemplateRows: 'auto 1fr',
          gridTemplateColumns: '1fr 1fr',
        }}
      >
        <Stack>
          <ReportBreadcrumbs title={REPORT_TITLE} />
          <AssetPresenceReportEducationalTitle
            title={REPORT_TITLE}
            reportKind="transit"
          />
        </Stack>
        <ReportDateRangeInput
          interval={transitReport.interval}
          timezone={user.organization.timezone}
          onChange={handleChangeDateRange}
          hasDateRange={hasDateRange}
          shortcuts={ASSET_PRESENCE_REPORT_DATE_RANGE_SHORTCUTS}
          error={isDateRangeTooLong || isDateRangeError}
          helperText={
            isDateRangeTooLong
              ? `Maximum date range is ${ASSET_PRESENCE_REPORT_MAX_DAYS} days`
              : undefined
          }
        >
          {isDateRangeError && (
            <Tooltip
              arrow
              title="The maximum date range for this report is three years"
            >
              <ErrorRounded color="error" fontSize="small" />
            </Tooltip>
          )}
        </ReportDateRangeInput>
        <Box ref={containerRef} gridColumn="span 2">
          {!hasDateRange ? (
            <Typography
              color="text.secondary"
              sx={{ py: 4, textAlign: 'center' }}
            >
              Select a date range to run the asset report
            </Typography>
          ) : isDateRangeTooLong ? (
            <Typography
              color="text.secondary"
              sx={{ py: 4, textAlign: 'center' }}
            >
              Please select a date range of {ASSET_PRESENCE_REPORT_MAX_DAYS}{' '}
              days or fewer to view this report.
            </Typography>
          ) : showLoader ? (
            <AssetReportLoader
              reportType="transit"
              periodCount={dayCount}
              isDataReady={!transitReport.isLoading}
              onComplete={handleLoaderComplete}
            />
          ) : (
            <AssetTransitReportTable
              data={data}
              interval={transitReport.interval}
              dayCount={transitReport.data?.dayCount ?? 0}
              isLoading={transitReport.isLoading}
              maximumHeight={containerSize.height}
              getFileName={getFileName}
            />
          )}
        </Box>
      </PageContent>
    </>
  );
}
